/**
 * Re-resolve setName / setCode / rarity / finish for cards that landed with
 * an empty setCode, then fire `bulkUpdateCards` patches. Used in two places:
 *
 *   - On hydration (AppContext.tsx) — sweeps existing inventory so legacy
 *     bad-bucket imports auto-correct on next load.
 *   - In the background after a CSV import (tools.import.tsx) — sequenced
 *     AFTER the create POST resolves so the PATCHes can't race ahead of the
 *     cards' creation (a PATCH that beats the POST silently no-ops
 *     server-side, which previously dropped setCode intermittently).
 *
 * Two phases, cheapest first:
 *
 *   Phase 1 — setName → setCode. One `resolveSetCodeByName` lookup per unique
 *     setName regardless of card count. Handles the common case where the
 *     CSV's set name is a real pokemontcg.io set ("Shrouded Fable").
 *
 *   Phase 2 — name + cardNumber fallback. Only for cards STILL without a
 *     setCode (coarse distribution labels like "Deck Exclusives" / "Sword &
 *     Shield Promo" that aren't real sets). Deduped by `name|cardNumber`,
 *     concurrency-limited so a stack of duplicates triggers one network call.
 *
 * Cards with a non-empty setCode are left alone — if the user pinned a set
 * manually, respect it.
 */
import type {
  Card,
  CardFinish,
  CardPatch,
  CardRarity,
} from "@/context/AppContext";
import {
  mapFinish,
  mapRarity,
  searchCardByNameAndNumber,
} from "@/features/tcgApi";
import { resolveSetCodeByName } from "@/features/setNameResolver";
import { resolveMtgSetCodeByName } from "@/features/mtgSetsCatalog";
import { splitMtgTypeLine } from "@tcg-web/normalize";

export interface SetBackfillContext {
  bulkUpdateCards: (patches: CardPatch[]) => void;
  /** Default 4. */
  concurrency?: number;
  /** Optional cancel signal — set true to halt the in-flight loop. The
   *  worker checks it between calls; partial results already patched stay. */
  cancelled?: { current: boolean };
}

interface Resolution {
  setId: string;
  setName: string;
  rarity: CardRarity;
  finish?: CardFinish;
  supertype?: string;
  subtypes?: string[];
  types?: string[];
  artist?: string;
  language?: string;
  /** YYYY sliced from the resolver's `releasedAt` (e.g. "2024-02-09" → "2024"). */
  year?: string;
  // TNK-121 MTG-native fields. Empty/undefined when the card isn't Magic.
  mtgManaCost?: string;
  mtgPowerToughness?: string;
  mtgLoyalty?: string;
  mtgOracleText?: string;
  mtgColors?: string[];
}

/** YYYY out of an ISO date string; null when the date is empty / malformed. */
function yearFrom(releasedAt?: string): string | undefined {
  if (!releasedAt) return undefined;
  const m = /^(\d{4})/.exec(releasedAt);
  return m ? m[1] : undefined;
}

/** Map a Scryfall `finishes` array onto the `Card.finish` vocabulary.
 *  `["foil"]` or `["etched"]` → "Premium" (closest Pokémon-vocabulary
 *  analog). `["nonfoil"]` → undefined. Mixed (`["nonfoil","foil"]`) →
 *  undefined too — the operator picks per-card at sort time, we don't
 *  guess. Returns undefined when the array is empty or only nonfoil. */
function preferredFinish(finishes?: string[]): CardFinish | undefined {
  if (!finishes || finishes.length === 0) return undefined;
  const onlyFoil = finishes.length === 1 && finishes[0] === "foil";
  const onlyEtched = finishes.length === 1 && finishes[0] === "etched";
  if (onlyFoil || onlyEtched) return "Premium";
  return undefined;
}

const hasSetCode = (c: Card): boolean =>
  Boolean(c.setCode && c.setCode.trim().length > 0);

const cardKey = (c: Card): string =>
  // TNK-123: collector number can be empty for legacy Magic — fall
  // back to setName so the dedupe key still distinguishes "Evil
  // Presence in Fifth Edition" from "Evil Presence in Fourth Edition".
  `${c.name.toLowerCase()}|${c.cardNumber.toLowerCase()}|${(c.setName ?? "").toLowerCase()}`;

/**
 * Phase 2 core: for every card with an empty setCode, look it up on
 * pokemontcg.io by (name, cardNumber) and return a Map keyed by
 * `name|cardNumber`.
 */
async function resolveByNameAndNumber(
  cards: Card[],
  concurrency: number,
  isCancelled: () => boolean,
): Promise<Map<string, Resolution>> {
  const seen = new Set<string>();
  const todo: Card[] = [];
  for (const c of cards) {
    if (hasSetCode(c) || !c.name) continue;
    // TNK-123: legacy Magic cards (pre-Mirage) can lack a collector
    // number; allow them through when setName is populated so the
    // server-side name+set fallback can resolve them.
    const allowMagicNameOnly =
      c.game === "Magic" && !c.cardNumber && !!c.setName?.trim();
    if (!c.cardNumber && !allowMagicNameOnly) continue;
    const key = cardKey(c);
    if (seen.has(key)) continue;
    seen.add(key);
    todo.push(c);
  }
  const updates = new Map<string, Resolution>();
  if (todo.length === 0) return updates;

  let cursor = 0;
  const worker = async () => {
    while (cursor < todo.length && !isCancelled()) {
      const card = todo[cursor++];
      const resolved = await searchCardByNameAndNumber(
        card.name,
        card.cardNumber,
        card.game,
        card.setName,
      );
      if (isCancelled()) return;
      if (!resolved) continue;
      // TNK-120: for Magic, split the Scryfall `type_line` into proper
      // supertype + types + subtypes (the old `parseCardTypes` only kept
      // the left side of the dash, losing creature subtypes like "Goblin
      // Warrior"). Pokémon's resolver fills `supertype` / `subtypes`
      // directly, so the splitter's output stays empty and we keep
      // whatever pokemontcg.io returned.
      const isMagic = card.game === "Magic";
      const split = isMagic ? splitMtgTypeLine(resolved.typeLine) : null;
      // For Magic, the type-line splitter only emits a true supertype
      // for cards that have one (Legendary / Snow / Basic / Tribal /
      // Ongoing / Host / Elite). Cards like "Sorcery" or "Enchantment
      // — Aura" return supertype=undefined. The storefront's Card
      // Type filter and the inventory chips both key off
      // tcg_supertype though, so fall back to the FIRST primary type
      // (Creature / Sorcery / Enchantment / etc.) when no MTG
      // supertype exists. Matches the AppContext Pass 3 path's
      // behavior for cards that already have a setCode.
      const supertype = isMagic
        ? (split?.supertype ?? split?.types[0] ?? undefined)
        : resolved.supertype;
      const subtypes = isMagic
        ? split && split.subtypes.length > 0
          ? split.subtypes
          : undefined
        : resolved.subtypes;
      const types = isMagic
        ? split && split.types.length > 0
          ? split.types
          : undefined
        : undefined;
      updates.set(cardKey(card), {
        setId: resolved.setId,
        setName: resolved.setName,
        rarity: mapRarity(
          resolved.rarity,
          resolved.supertype,
          resolved.subtypes,
          resolved.hp,
        ),
        finish:
          preferredFinish(resolved.finishes) ??
          mapFinish(
            resolved.rarity,
            resolved.supertype,
            resolved.subtypes,
            resolved.hp,
          ),
        supertype,
        subtypes,
        types,
        artist: resolved.artist,
        language: resolved.language,
        year: yearFrom(resolved.releasedAt),
        mtgManaCost: resolved.mtgManaCost,
        mtgPowerToughness: resolved.mtgPowerToughness,
        mtgLoyalty: resolved.mtgLoyalty,
        mtgOracleText: resolved.mtgOracleText,
        mtgColors: resolved.mtgColors,
      });
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, todo.length) }, () => worker()),
  );
  return updates;
}

export async function backfillCardSets(
  cards: Card[],
  ctx: SetBackfillContext,
): Promise<void> {
  const isCancelled = () => ctx.cancelled?.current === true;
  const patches: CardPatch[] = [];

  // Phase 1 — setName → setCode (fast batch, one call per unique
  // (game, name) tuple). Dispatch by game so Magic names hit the
  // local mtg_sets_catalog cache (TNK-129) instead of the
  // pokemontcg.io path that only knows Pokémon sets; collapses a
  // 50-card Ice Age import from 50 Scryfall calls into 1 cache hit.
  const uniquePairs = new Set<string>();
  const pairs: Array<{ name: string; game: string }> = [];
  for (const c of cards) {
    if (hasSetCode(c) || !c.setName?.trim()) continue;
    const key = `${c.game}|${c.setName}`;
    if (uniquePairs.has(key)) continue;
    uniquePairs.add(key);
    pairs.push({ name: c.setName, game: c.game });
  }
  const setCodeByName = new Map<string, string | null>();
  await Promise.all(
    pairs.map(async ({ name, game }) => {
      const code =
        game === "Magic"
          ? await resolveMtgSetCodeByName(name)
          : await resolveSetCodeByName(name);
      // Key by `(game, name)` so a same-named set in two games (rare
      // but legal) doesn't collide.
      setCodeByName.set(`${game}|${name}`, code);
    }),
  );
  if (isCancelled()) return;

  const stillEmpty: Card[] = [];
  for (const c of cards) {
    if (hasSetCode(c) || !c.name) continue;
    // TNK-123: legacy Magic cards (pre-Mirage) can lack a collector
    // number; allow them through when setName is populated so Phase 2
    // can hand them to the name+set Scryfall fallback.
    const allowMagicNameOnly =
      c.game === "Magic" && !c.cardNumber && !!c.setName?.trim();
    if (!c.cardNumber && !allowMagicNameOnly) continue;
    const code = c.setName ? setCodeByName.get(`${c.game}|${c.setName}`) : null;
    if (code) {
      patches.push({ id: c.id, patch: { setCode: code } });
    } else {
      stillEmpty.push(c);
    }
  }

  // Phase 2 — name + number fallback for the leftovers (distribution labels).
  const updates = await resolveByNameAndNumber(
    stillEmpty,
    ctx.concurrency ?? 4,
    isCancelled,
  );
  if (isCancelled()) return;
  for (const c of stillEmpty) {
    const u = updates.get(cardKey(c));
    if (!u) continue;
    patches.push({
      id: c.id,
      patch: {
        setCode: u.setId,
        setName: u.setName,
        rarity: u.rarity,
        finish: u.finish,
        ...(u.supertype && !c.tcgSupertype
          ? { tcgSupertype: u.supertype }
          : {}),
        ...(u.subtypes && !c.tcgSubtypes ? { tcgSubtypes: u.subtypes } : {}),
        ...(u.types && !c.tcgTypes ? { tcgTypes: u.types } : {}),
        // TNK-120: artist / year / language only filled when the operator
        // hasn't manually entered them. `finish` above already wins from
        // Scryfall, so the operator can override post-import.
        ...(u.artist && !c.tcgArtist ? { tcgArtist: u.artist } : {}),
        ...(u.year && !c.year ? { year: u.year } : {}),
        ...(u.language && !c.language ? { language: u.language } : {}),
        // TNK-121 MTG-native fields. Same guard pattern — only fill when
        // the operator hasn't already entered something. Mana cost can be
        // an empty string for Lands; treat as truthy only when non-empty.
        ...(u.mtgManaCost && !c.mtgManaCost
          ? { mtgManaCost: u.mtgManaCost }
          : {}),
        ...(u.mtgPowerToughness && !c.mtgPowerToughness
          ? { mtgPowerToughness: u.mtgPowerToughness }
          : {}),
        ...(u.mtgLoyalty && !c.mtgLoyalty ? { mtgLoyalty: u.mtgLoyalty } : {}),
        ...(u.mtgOracleText && !c.mtgOracleText
          ? { mtgOracleText: u.mtgOracleText }
          : {}),
        ...(u.mtgColors && !c.mtgColors ? { mtgColors: u.mtgColors } : {}),
      },
    });
  }

  if (patches.length > 0) ctx.bulkUpdateCards(patches);
}
